import { useEffect, useState } from "react";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUSES = [
  { key: "en_attente", label: "En attente", cls: "badge-fav" },
  { key: "en_preparation", label: "En préparation", cls: "badge-hot" },
  { key: "prete", label: "Prête", cls: "badge-veg" },
  { key: "recuperee", label: "Récupérée", cls: "" },
];

const EMPTY = { customer_name: "", phone: "", items: "", pickup_time: "" };

export default function OrderManager() {
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/orders", form);
      toast.success("Commande enregistrée");
      setForm({ ...EMPTY });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (o, status) => {
    try {
      await api.patch(`/orders/${o.id}`, { status });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (o) => {
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Commande supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const active = orders.filter((o) => o.status !== "recuperee");
  const todayCount = orders.filter((o) => new Date(o.created_at).toDateString() === new Date().toDateString()).length;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div data-testid="admin-stat-today" className="rounded-2xl border border-border/60 bg-surface-2 p-5">
          <p className="font-code text-[10px] uppercase tracking-[0.25em] text-dim">Commandes aujourd'hui</p>
          <p className="font-display mt-2 text-3xl font-bold text-ochre">{todayCount}</p>
        </div>
        <div data-testid="admin-stat-active" className="rounded-2xl border border-border/60 bg-surface-2 p-5">
          <p className="font-code text-[10px] uppercase tracking-[0.25em] text-dim">En cours</p>
          <p className="font-display mt-2 text-3xl font-bold text-flame">{active.length}</p>
        </div>
        <div className="flex items-center justify-center rounded-2xl border border-border/60 bg-surface-2 p-5">
          <button data-testid="admin-orders-refresh" onClick={load} className="btn-ghost !py-2.5 !px-5 text-sm">
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      <form data-testid="admin-order-form" onSubmit={add} className="mt-6 rounded-2xl border border-border/60 bg-surface-2 p-6">
        <h3 className="font-display text-base font-bold">Nouvelle commande téléphonique</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <input data-testid="order-form-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nom du client" className="admin-input" />
          <input data-testid="order-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="admin-input" />
          <input data-testid="order-form-items" required value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} placeholder="Contenu (ex : 1 Regina, 1 Tiramisu)" className="admin-input sm:col-span-2" />
          <input data-testid="order-form-pickup" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} placeholder="Heure de retrait (ex : 19h30)" className="admin-input" />
          <button data-testid="order-form-submit" type="submit" disabled={saving} className="btn-primary justify-center !py-2.5 text-sm disabled:opacity-60">
            <Plus size={15} /> {saving ? "Ajout…" : "Enregistrer la commande"}
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {orders.map((o) => {
          const st = STATUSES.find((s) => s.key === o.status) || STATUSES[0];
          return (
            <div key={o.id} data-testid={`admin-order-card-${o.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-border/60 bg-surface-2 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">
                  {o.customer_name}
                  {o.pickup_time && <span className="ml-2 font-code text-xs text-ochre">Retrait {o.pickup_time}</span>}
                </p>
                <p className="truncate text-xs text-dim">{o.items}{o.phone ? ` · ${o.phone}` : ""}</p>
              </div>
              <span className={`badge-tag ${st.cls} ${!st.cls ? "border-border text-dim" : ""}`}>{st.label}</span>
              <select
                data-testid={`admin-order-status-select-${o.id}`}
                value={o.status}
                onChange={(e) => setStatus(o, e.target.value)}
                className="admin-input !w-auto !py-1.5 text-xs"
              >
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button data-testid={`admin-order-delete-${o.id}`} onClick={() => remove(o)} className="rounded-full border border-border p-2 text-dim hover:border-flame hover:text-flame transition-colors" aria-label="Supprimer">
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
        {orders.length === 0 && <p className="rounded-2xl border border-border/60 bg-surface-2 px-5 py-10 text-center text-sm text-dim">Aucune commande pour le moment.</p>}
      </div>
    </div>
  );
}
